import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import type { AaiRequest, BandSum, PortfolioRecommendation, PortfolioRecommendationStatus } from '../../types/aai';
import type { ChipTone } from '../../components/aa';
import { Action, Card, Chip, Divided, Drawer, EmptyState, Field, Input, Kicker, ListRow, Panel, PanelHead, Select } from '../../components/aa';
import { Tiles } from '../owner/ControlDrawers';
import { relativeTime } from '../partner/format';
import { compactMoney, isMaterialRisk, kindOf, money, needsChampion, parseBand, plural, quarterLabel, valueLine } from './champion';
import { shortName } from './actionStatus';

/*
 * The portfolio recommendation, both sides of it.
 *
 * The AI Champion composes one from the live ranking and sends it; the Owner it
 * was sent to accepts it, accepts it with an adjusted setup limit, or declines
 * it. Every screen here reads the record the server returned, never a local
 * guess: the compose drawer flips to its sent state only when the POST came
 * back, and the decision card flips only when the response was stored.
 *
 * The recommendation is a portfolio-level decision and says so everywhere it
 * can be acted on: direction and a setup limit, not an approval of any Project,
 * Quick Win, access, membership, release or security decision. Those keep
 * their own records under Decisions needing you.
 */

export const periodLabel = (): string => `${quarterLabel()} ${new Date().getFullYear()}`;

export const STATUS_LOOK: Record<PortfolioRecommendationStatus, { label: string; tone: ChipTone }> = {
  sent: { label: 'Sent · awaiting decision', tone: 'warn' },
  accepted: { label: 'Accepted', tone: 'ok' },
  adjusted: { label: 'Accepted with adjusted limit', tone: 'ok' },
  declined: { label: 'Declined', tone: 'err' },
  superseded: { label: 'Superseded', tone: 'neutral' },
};

export const statusChip = (status: PortfolioRecommendationStatus): React.ReactNode => {
  const look = STATUS_LOOK[status] || { label: status, tone: 'neutral' as ChipTone };
  return <Chip tone={look.tone} dot={status === 'sent'}>{look.label}</Chip>;
};

export const recTitle = (rec: PortfolioRecommendation): string => `${rec.period} recommendation v${rec.version}`;

export const isOpenFor = (rec: PortfolioRecommendation, me: string): boolean => (
  rec.status === 'sent' && rec.sent_to.toLowerCase() === me.toLowerCase()
);

const NO_FORECAST = 'No forecast recorded';
const NO_SETUP = 'No setup estimate recorded';

const rangeLine = (sum: BandSum, empty: string, suffix: string): string => {
  if (sum.low === null || sum.high === null) return empty;
  let line = `${compactMoney(sum.low)}–${compactMoney(sum.high)}${suffix}`;
  if (sum.without > 0) line += ` · ${sum.without} without estimate`;
  return line;
};

export const forecastLine = (sum: BandSum): string => rangeLine(sum, NO_FORECAST, ' / yr expected');
export const setupLine = (sum: BandSum): string => rangeLine(sum, NO_SETUP, ' setup');

/** The same sum the server stores, computed from live bands for the compose preview. */
export const sumBands = (rows: AaiRequest[], pick: (r: AaiRequest) => string | null | undefined): BandSum => {
  let low = 0;
  let high = 0;
  let counted = 0;
  rows.forEach((r) => {
    const band = parseBand(pick(r));
    if (!band) return;
    low += band.low;
    high += band.high;
    counted += 1;
  });
  if (counted === 0) return { low: null, high: null, counted: 0, without: rows.length };
  return { low, high, counted, without: rows.length - counted };
};

export const confirmationOf = (r: AaiRequest): { label: string; tone: ChipTone } => {
  if (r.tool_skill_id) return { label: 'In use', tone: 'ok' };
  if (r.status === 'approved') return { label: 'Confirmed · in build', tone: 'ok' };
  if (needsChampion(r)) return { label: 'Awaiting your confirmation', tone: 'warn' };
  if (r.status === 'needs_info') return { label: 'Detail requested', tone: 'neutral' };
  if (r.status === 'pending' && r.reviewer_gate !== 'company') return { label: 'With the department head', tone: 'neutral' };
  return { label: 'Waiting', tone: 'neutral' };
};

const limitText = (amount: number | null, currency: string): string => {
  if (amount === null) return 'Not set';
  return money(amount, currency);
};

const errorDetail = (err: unknown, fallback: string): string => {
  const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  return fallback;
};

const TEXTAREA = 'w-full rounded-act border border-input bg-white px-3 py-2 text-[13px] text-ink outline-none transition-colors focus:border-ink focus:ring-[3px] focus:ring-ink/10 resize-y';

const SCOPE_NOTE = 'A portfolio-level decision only: which priorities to focus on and the setup limit. It approves no Project, Quick Win, '
  + 'data or source access, Workspace membership, release or security decision. Each Project keeps its own decision under Decisions needing you.';

const Rank: React.FC<{ n: number }> = ({ n }) => (
  <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-canvas-inset font-sans text-[12px] font-bold tabular-nums text-ink">{n}</span>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{children}</div>
);

const Note: React.FC<{ label: string; by: string; when: string | null; text: string }> = ({ label, by, when, text }) => (
  <div className="mt-6">
    <SectionLabel>{label}</SectionLabel>
    <Card pad="md">
      <p className="m-0 text-[13px] leading-[21px] text-ink">{text}</p>
      <div className="mt-2 text-[12px] text-ink-dim">{shortName(by)} · {relativeTime(when)}</div>
    </Card>
  </div>
);

// ── Champion: compose and send ───────────────────────────────────────────────

export const ComposeDrawer: React.FC<{
  plan: AaiRequest[];
  executives: string[];
  onSent: (rec: PortfolioRecommendation) => void;
  onOpenProject: (id: string) => void;
  onClose: () => void;
}> = ({ plan, executives, onSent, onOpenProject, onClose }) => {
  const [sentTo, setSentTo] = useState(executives[0] || '');
  const [limit, setLimit] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<PortfolioRecommendation | null>(null);

  const forecast = sumBands(plan, (r) => r.est_value_band);
  const setup = sumBands(plan, (r) => r.est_cost_band);
  const risky = plan.filter(isMaterialRisk).length;
  let riskLine = 'None flagged';
  if (risky > 0) riskLine = `${plural(risky, 'priority', 'priorities')} flagged high risk`;
  const amount = Number(limit);
  const limitOk = Number.isFinite(amount) && amount > 0;

  let sendReason = 'Set the recommended setup limit';
  if (!sentTo) sendReason = 'No Owner is recorded for this company yet';
  if (busy) sendReason = 'Sending';

  const send = async () => {
    setBusy(true);
    try {
      const res = await aaiApi.sendPortfolioRecommendation({
        period: periodLabel(),
        request_ids: plan.map((r) => r.id),
        recommended_limit: amount,
        currency: 'USD',
        note: note.trim() || undefined,
        sent_to: sentTo,
      });
      setSent(res.data);
      onSent(res.data);
      window.dispatchEvent(new Event('aai:notifications-changed'));
    } catch (err) {
      toast.error(errorDetail(err, 'That recommendation did not send.'));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Drawer
        onClose={onClose}
        kicker={<Kicker>Portfolio recommendation</Kicker>}
        badge={statusChip(sent.status)}
        title="Recommendation sent"
        sub={`${recTitle(sent)} went to ${shortName(sent.sent_to)} ${relativeTime(sent.sent_at)}. They decide on their Home; you are notified when they answer.`}
        footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>}
      >
        <Tiles
          facts={[
            { label: 'Sent to', value: sent.sent_to },
            { label: 'Recommended setup limit', value: limitText(sent.recommended_limit, sent.currency) },
            { label: 'Forecast value, not confirmed', value: forecastLine(sent.forecast_value) },
            { label: 'Priorities', value: `${sent.items.length}` },
          ]}
        />
        <p className="m-0 mt-5 text-[12.5px] leading-[20px] text-ink-dim">{SCOPE_NOTE}</p>
      </Drawer>
    );
  }

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Portfolio recommendation</Kicker>}
      badge={<Chip>Not sent yet</Chip>}
      title={`Recommended ${periodLabel()} priorities`}
      sub="From the live ranking. Sending stores a snapshot of these priorities and their forecasts as they read now, and asks the Owner for a portfolio-level decision."
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>
          <Action size="lg" onClick={send} disabled={busy || !limitOk || !sentTo} reason={sendReason}>Send to Executive</Action>
        </div>
      )}
    >
      <Tiles
        facts={[
          { label: 'Recommended priorities', value: `${plan.length}` },
          { label: 'Forecast value, not confirmed', value: forecastLine(forecast) },
          { label: 'Setup range, estimated', value: setupLine(setup) },
          { label: 'Material risk', value: riskLine },
        ]}
      />

      <div className="mt-6">
        <SectionLabel>Recommended priorities</SectionLabel>
        <div className="rounded-card border border-line">
          <Divided>
            {plan.map((r, i) => {
              const state = confirmationOf(r);
              let right: React.ReactNode = null;
              if (isMaterialRisk(r)) right = <Chip tone="err" dot>High risk</Chip>;
              return (
                <ListRow
                  key={r.id}
                  mark={<Rank n={i + 1} />}
                  title={r.title}
                  badge={<Chip tone={state.tone}>{state.label}</Chip>}
                  foot={`${kindOf(r)} · ${r.department || 'Whole company'} · ${valueLine(r)}`}
                  right={right}
                  onClick={() => onOpenProject(r.id)}
                  arrow
                />
              );
            })}
          </Divided>
        </div>
      </div>

      <div className="mt-6 space-y-5">
        <Field label="Executive decision owner" htmlFor="rec-owner" hint="An Owner of this company. Only they can answer this recommendation.">
          <Select id="rec-owner" value={sentTo} onChange={(e) => setSentTo(e.target.value)} disabled={executives.length === 0}>
            {executives.length === 0 && <option value="">No Owner recorded</option>}
            {executives.map((email) => <option key={email} value={email}>{email}</option>)}
          </Select>
        </Field>
        <Field label="Recommended setup limit (USD)" htmlFor="rec-limit" hint="The most you recommend the company commits to setting these priorities up this period. The Owner may adjust it.">
          <Input id="rec-limit" type="number" min={1} step={1000} inputMode="numeric" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="300000" />
        </Field>
        <Field label="Recommendation note" htmlFor="rec-note" hint="Why this mix, in a few sentences. The Owner reads it as your rationale.">
          <textarea id="rec-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={TEXTAREA} />
        </Field>
      </div>

      <p className="m-0 mt-5 text-[12.5px] leading-[20px] text-ink-dim">{SCOPE_NOTE}</p>
    </Drawer>
  );
};

// ── Both sides: the record, with the Owner's decision when it is theirs ─────

type DecisionMode = 'read' | 'adjusted' | 'declined';

const DecisionCard: React.FC<{ rec: PortfolioRecommendation; onDecided: (rec: PortfolioRecommendation) => void }> = ({ rec, onDecided }) => {
  const [mode, setMode] = useState<DecisionMode>('read');
  const [limit, setLimit] = useState(rec.recommended_limit === null ? '' : `${rec.recommended_limit}`);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const amount = Number(limit);
  const limitOk = Number.isFinite(amount) && amount > 0;

  const decide = async (status: 'accepted' | 'adjusted' | 'declined') => {
    if (status !== 'accepted' && !note.trim()) {
      toast.error('Say why, in a sentence. The AI Champion reads it.');
      return;
    }
    setBusy(true);
    try {
      const body: { status: 'accepted' | 'adjusted' | 'declined'; final_limit?: number; note?: string } = { status };
      if (status === 'adjusted') body.final_limit = amount;
      if (note.trim()) body.note = note.trim();
      const res = await aaiApi.respondPortfolioRecommendation(rec.id, body);
      toast.success(`Recommendation ${STATUS_LOOK[res.data.status].label.toLowerCase()}.`);
      window.dispatchEvent(new Event('aai:notifications-changed'));
      onDecided(res.data);
    } catch (err) {
      toast.error(errorDetail(err, 'That decision did not save.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card pad="md" className="mt-6">
      <div className="flex items-center gap-2.5">
        <span className="text-[13px] font-semibold text-ink">Your decision</span>
        <Chip tone="warn" dot>Yours to decide</Chip>
      </div>
      <p className="m-0 mt-2 text-[12.5px] leading-[20px] text-ink-dim">{SCOPE_NOTE}</p>
      {mode === 'read' && (
        <div className="mt-4 flex flex-wrap gap-3">
          <Action onClick={() => decide('accepted')} disabled={busy} reason="Saving">Accept</Action>
          <Action variant="quiet" onClick={() => setMode('adjusted')}>Adjust the limit</Action>
          <Action variant="quiet" onClick={() => setMode('declined')}>Decline with a note</Action>
        </div>
      )}
      {mode === 'adjusted' && (
        <div className="mt-4 space-y-4">
          <Field label="Adjusted setup limit (USD)" htmlFor="rec-final-limit" hint={`Recommended: ${limitText(rec.recommended_limit, rec.currency)}`}>
            <Input id="rec-final-limit" type="number" min={1} step={1000} inputMode="numeric" value={limit} onChange={(e) => setLimit(e.target.value)} />
          </Field>
          <Field label="Response note" htmlFor="rec-adjust-note">
            <textarea id="rec-adjust-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={TEXTAREA} placeholder="Why the limit changes" />
          </Field>
          <div className="flex flex-wrap gap-3">
            <Action onClick={() => decide('adjusted')} disabled={busy || !limitOk} reason="Set the adjusted limit">Accept with this limit</Action>
            <Action variant="quiet" onClick={() => setMode('read')}>Back</Action>
          </div>
        </div>
      )}
      {mode === 'declined' && (
        <div className="mt-4 space-y-4">
          <Field label="Response note" htmlFor="rec-decline-note">
            <textarea id="rec-decline-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={TEXTAREA} placeholder="What should change before you would accept" />
          </Field>
          <div className="flex flex-wrap gap-3">
            <Action onClick={() => decide('declined')} disabled={busy} reason="Saving">Decline</Action>
            <Action variant="quiet" onClick={() => setMode('read')}>Back</Action>
          </div>
        </div>
      )}
    </Card>
  );
};

export const RecordDrawer: React.FC<{
  rec: PortfolioRecommendation;
  me: string;
  onChanged: () => void;
  onOpenProject?: (id: string) => void;
  onClose: () => void;
}> = ({ rec: initial, me, onChanged, onOpenProject, onClose }) => {
  const [rec, setRec] = useState(initial);
  const mine = isOpenFor(rec, me);
  const closed = rec.status !== 'sent';

  let finalLabel = 'Final setup limit';
  let finalValue = limitText(rec.final_limit, rec.currency);
  if (rec.status === 'sent') {
    finalLabel = 'Decision';
    finalValue = `Waiting on ${shortName(rec.sent_to)}`;
  }
  if (rec.status === 'declined') finalValue = 'Declined, no limit set';
  if (rec.status === 'superseded') {
    finalLabel = 'Decision';
    finalValue = 'Replaced by a newer recommendation';
  }

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Portfolio recommendation</Kicker>}
      badge={statusChip(rec.status)}
      title={recTitle(rec)}
      sub={`Sent by ${shortName(rec.sent_by)} to ${shortName(rec.sent_to)} ${relativeTime(rec.sent_at)}. Figures are the snapshot as sent; each Project record shows its live values.`}
      footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>}
    >
      <Tiles
        facts={[
          { label: 'Forecast value, not confirmed', value: forecastLine(rec.forecast_value) },
          { label: 'Setup range, estimated', value: setupLine(rec.setup_range) },
          { label: 'Recommended setup limit', value: limitText(rec.recommended_limit, rec.currency) },
          { label: finalLabel, value: finalValue },
        ]}
      />

      {rec.note && <Note label="Recommendation note" by={rec.sent_by} when={rec.sent_at} text={rec.note} />}
      {closed && rec.response_note && rec.responded_by && (
        <Note label="Owner's response" by={rec.responded_by} when={rec.responded_at} text={rec.response_note} />
      )}

      <div className="mt-6">
        <SectionLabel>Priorities as sent</SectionLabel>
        <div className="rounded-card border border-line">
          <Divided>
            {rec.items.map((item) => {
              let band = 'Forecast not recorded';
              if (item.forecast_value_band) band = `${item.forecast_value_band} expected yearly value`;
              let right: React.ReactNode = null;
              if ((item.risk || '').toLowerCase() === 'high') right = <Chip tone="err" dot>High risk</Chip>;
              let onClick: (() => void) | undefined;
              if (onOpenProject) onClick = () => onOpenProject(item.request_id);
              return (
                <ListRow
                  key={item.request_id}
                  mark={<Rank n={item.rank} />}
                  title={item.title}
                  foot={`${item.kind} · ${item.department || 'Whole company'} · ${band}`}
                  right={right}
                  onClick={onClick}
                  arrow={Boolean(onOpenProject)}
                />
              );
            })}
          </Divided>
        </div>
        <p className="m-0 mt-2 text-[12px] leading-[20px] text-ink-dim">Open a row for the Project record and its own decision. Deciding here changes none of them.</p>
      </div>

      {mine && <DecisionCard rec={rec} onDecided={(updated) => { setRec(updated); onChanged(); }} />}
      {!mine && rec.status === 'sent' && (
        <p className="m-0 mt-6 text-[12.5px] leading-[20px] text-ink-dim">Only {rec.sent_to} can answer this recommendation.</p>
      )}
    </Drawer>
  );
};

// ── History ──────────────────────────────────────────────────────────────────

export const RecommendationHistory: React.FC<{
  recs: PortfolioRecommendation[];
  onOpen: (rec: PortfolioRecommendation) => void;
  emptyReason: string;
  className?: string;
}> = ({ recs, onOpen, emptyReason, className = '' }) => (
  <Panel className={className}>
    <PanelHead
      kicker="Portfolio recommendations"
      title="What the AI Champion recommended, and what the Owner decided"
      desc="Every recommendation stays here as sent. Figures are snapshots; the Project records show live values."
    />
    {recs.length === 0 && <EmptyState title="None sent yet" reason={emptyReason} />}
    <Divided>
      {recs.map((rec) => {
        const parts = [`To ${shortName(rec.sent_to)}`, relativeTime(rec.sent_at), plural(rec.items.length, 'priority', 'priorities')];
        if (rec.recommended_limit !== null) parts.push(`${limitText(rec.recommended_limit, rec.currency)} recommended`);
        if ((rec.status === 'accepted' || rec.status === 'adjusted') && rec.final_limit !== null) parts.push(`${limitText(rec.final_limit, rec.currency)} final`);
        return (
          <ListRow
            key={rec.id}
            mark={<Rank n={rec.version} />}
            title={recTitle(rec)}
            badge={statusChip(rec.status)}
            desc={rec.response_note || rec.note || undefined}
            foot={parts.join(' · ')}
            onClick={() => onOpen(rec)}
            arrow
          />
        );
      })}
    </Divided>
  </Panel>
);
